import React from 'react';
import { FaDownload } from 'react-icons/fa6';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';

export interface TreeNode {
  label: string;
  count: number;
  children?: TreeNode[];
}

interface Props {
  title: string;
  subtitle?: string;
  sections: TreeNode[];
  callout?: { label: string; count: number };
  loading: boolean;
  error: string | null;
  onDownloadCsv: () => void | Promise<void>;
  parentPage?: string;
  parentPath?: string;
}

const TreeRow: React.FC<{ node: TreeNode; depth: number }> = ({ node, depth }) => {
  const indentRem = depth * 1.25;
  return (
    <>
      <div className='flex items-baseline justify-between border-t border-stroke py-1.5'>
        <span
          className={depth === 0 ? 'font-semibold text-sm' : 'text-sm text-body'}
          style={{ paddingLeft: `${indentRem}rem` }}
        >
          {node.label}
        </span>
        <span className={depth === 0 ? 'font-semibold tabular-nums' : 'tabular-nums text-body'}>
          {node.count.toLocaleString()}
        </span>
      </div>
      {node.children?.map((child, i) => (
        <TreeRow key={i} node={child} depth={depth + 1} />
      ))}
    </>
  );
};

export const PredefinedTreeLayout: React.FC<Props> = (props) => {
  const {
    title,
    subtitle,
    sections,
    callout,
    loading,
    error,
    onDownloadCsv,
    // Every predefined report is launched from the Predefined Reports tab, so
    // that is where the crumb goes back to.
    parentPage = 'Reports',
    parentPath = '/new-report?view=predefined',
  } = props;
  const hasData = !loading && !error && sections.length > 0;

  return (
    <>
      <Breadcrumb pageName={title} parentPage={parentPage} parentPath={parentPath} />
      <div className='rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5'>
        <div className='flex items-start justify-between mb-4'>
          <div>
            {subtitle && <p className='text-sm text-body'>{subtitle}</p>}
          </div>
          <button
            type='button'
            onClick={onDownloadCsv}
            disabled={loading || !hasData}
            className='inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50'
          >
            <FaDownload /> Download CSV
          </button>
        </div>

        {loading && <p className='py-12 text-center text-body'>Loading…</p>}
        {error && <p className='py-12 text-center text-danger'>{error}</p>}
        {!loading && !error && sections.length === 0 && (
          <p className='py-12 text-center text-body'>No results.</p>
        )}

        {hasData && (
          <div>
            {sections.map((section, idx) => (
              <div key={idx} className='mb-6 last:mb-0'>
                <TreeRow node={section} depth={0} />
              </div>
            ))}
            {callout && (
              <div className='mt-2 flex items-baseline justify-between border-t-2 border-primary pt-3'>
                <span className='font-semibold text-sm'>{callout.label}</span>
                <span className='font-semibold tabular-nums'>{callout.count.toLocaleString()}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
